import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { IoIosArrowBack, IoIosArrowDown } from "react-icons/io"
import { IoSearch } from "react-icons/io5"
import { MdSort } from "react-icons/md"
import { useLocation, useNavigate, useParams } from "react-router-dom"

import BackgroundWrapper from "@fuse/sharedComponents/backgroundWrapper"
import BlurWrapper from "@fuse/sharedComponents/blurWrapper"
import { blurKeys } from "@fuse/sharedComponents/blurWrapper/blurKeysList"
import InputField from "@fuse/sharedComponents/inputField"
import LoadingSpinner from "@fuse/sharedComponents/loader/LoadingSpinner"
import { getDialCode } from "@letshang/assets"
import {
  EFeatureFlags,
  EMessageStatus,
  EStatusType,
  EVENT_CLASS,
  EventStatusType,
  EventStatusType as GuestStatus,
  TEventDetail,
} from "@letshang/constants"
import { motion } from "framer-motion"
import { get } from "lodash-es"
import { useRecoilState } from "recoil"
import { ROUTES } from "src/App"

import { userState } from "@fuse/recoil/signup"

import { useProcessFeatureFlags } from "@settingsAlias/featureFlags"

import { useEventDetails } from "@api/services/events/useEventsApi"

import SearchBar from "../explore/component/SearchBar"
import EmptyGuestList from "./EmptyGuestList"
import GuestListSkeleton from "./GuestListSkeleton"
import ReviewGuests, { ReviewGuestsHandle } from "./ReviewGuests"

export type Guest = {
  uid: string
  name: string
  avatarUrl: string
  status: GuestStatus
  age: number
  phoneNumber: string
  email: string
  lastUpdatedAt: string
}

interface GuestListProps {
  guestlist: Guest[]
  eventDetails: TEventDetail
  invitedGuests: Guest[]
  messageRecords: any[]
  eventId: string
  isGuestListLoading: boolean
  handleApproveGuest: (guestDetails: any) => Promise<void>
  handleDisapproveGuest: (guestDetails: any) => Promise<void>
  handleCheckInGuest: (guestDetails: any) => Promise<void>
}

// Sort options enum
enum SortOption {
  NAME_A_Z = "name_a_z",
  NAME_Z_A = "name_z_a",
  MOST_RECENT = "most_recent",
}

const GuestListCore = ({
  guestlist,
  eventDetails,
  invitedGuests,
  messageRecords,
  eventId,
  isGuestListLoading,
  handleApproveGuest,
  handleDisapproveGuest,
  handleCheckInGuest,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const flyerImage = get(eventDetails, "eventDocData.flyerImage", "")
  const { state: locationState } = useLocation()

  const [user] = useRecoilState(userState)

  const navigate = useNavigate()
  const handleBackPress = () => {
    navigate(ROUTES.EVENTS.replace(":eventId", eventId))
  }

  const isTicketedEvent =
    eventDetails?.eventDocData?.eventClass === EVENT_CLASS.TICKETED_EVENT
  const [guests, setGuests] = useState(guestlist)
  const [invitedGuestList, setInvitedGuestList] = useState(invitedGuests)
  const [searchText, setSearchText] = useState("")
  const [sortOption, setSortOption] = useState<SortOption>(SortOption.NAME_A_Z)

  const statusKey = isTicketedEvent ? "applicationStatus" : "status"
  const sentStatuses = ["delivered", "read"]
  const failedStatuses = ["undelivered", "failed"]
  const processingStatuses = ["sent", "queued"]

  const guestListWeb = {
    isAwaitingEnabled: useProcessFeatureFlags(
      EFeatureFlags.GUEST_LIST_WEB_AWAITING,
    ),
    isInvitedEnabled: useProcessFeatureFlags(
      EFeatureFlags.GUEST_LIST_WEB_INVITED,
    ),
    isGoingEnabled: useProcessFeatureFlags(EFeatureFlags.GUEST_LIST_WEB_GOING),
    isNotGoingEnabled: useProcessFeatureFlags(
      EFeatureFlags.GUEST_LIST_WEB_NOT_GOING,
    ),
  }

  const { sentArray, failedArray, processingArray } = (messageRecords?.length &&
    messageRecords?.reduce(
      (acc, obj) => {
        if (obj.statuses.some((status) => sentStatuses.includes(status))) {
          acc.sentArray.push(obj)
        } else if (
          obj.statuses.some((status) => failedStatuses.includes(status))
        ) {
          acc.failedArray.push(obj)
        } else {
          acc.processingArray.push(obj)
        }
        return acc
      },
      { sentArray: [], failedArray: [], processingArray: [] },
    )) || { sentArray: [], failedArray: [], processingArray: [] }

  // Sort function for guests
  const sortGuests = useCallback(
    (guestArray: Guest[], sortBy: SortOption): Guest[] => {
      // Check if guestArray is a valid array
      if (!Array.isArray(guestArray) || guestArray.length === 0) {
        return []
      }

      const sortedArray = [...guestArray]

      switch (sortBy) {
        case SortOption.NAME_A_Z:
          return sortedArray.sort((a, b) => {
            const nameA = (a?.name || a?.email || "").toLowerCase()
            const nameB = (b?.name || b?.email || "").toLowerCase()
            return nameA.localeCompare(nameB)
          })

        case SortOption.NAME_Z_A:
          return sortedArray.sort((a, b) => {
            const nameA = (a?.name || a?.email || "").toLowerCase()
            const nameB = (b?.name || b?.email || "").toLowerCase()
            return nameB.localeCompare(nameA)
          })

        case SortOption.MOST_RECENT:
          // Assuming there's a createdAt or similar timestamp field
          // If not available, maintain original order
          return sortedArray.sort((a, b) => {
            const timeA = a?.lastUpdatedAt || 0
            const timeB = b?.lastUpdatedAt || 0
            return new Date(timeB).getTime() - new Date(timeA).getTime()
          })

        default:
          return sortedArray
      }
    },
    [],
  )

  // Memoize filtered and sorted guest categories
  const { accepted, declined, awaiting } = useMemo(() => {
    let acceptedPeopleCount = 0
    const acceptedGuests =
      guests?.filter((el: any) => {
        if (
          el?.[statusKey] === GuestStatus.ACCEPTED ||
          el?.[statusKey] === GuestStatus.CHECKED_IN ||
          el?.[statusKey] === GuestStatus.ATTENDING
        ) {
          acceptedPeopleCount += 1 + Number(el?.numberOfExtraJoinee)
          return true
        }
        return false
      }) || []

    const declinedGuests =
      guests?.filter(
        (el: any) =>
          el?.[statusKey] === GuestStatus.DECLINED ||
          el?.[statusKey] === GuestStatus.REFUNDED ||
          el?.[statusKey] === GuestStatus.REFUND_REQUESTED ||
          el?.[statusKey] === GuestStatus.LEFT ||
          el?.[statusKey] === GuestStatus.USER_DECLINED,
      ) || []

    let awaitingPeopleCount = 0
    const awaitingGuests =
      guests?.filter((el: any) => {
        if (el?.[statusKey] === GuestStatus.UNDER_REVIEW) {
          awaitingPeopleCount += 1 + Number(el?.numberOfExtraJoinee)
          return true
        }
        return false
      }) || []

    // Apply sorting to each category
    const accepted = sortGuests(acceptedGuests, sortOption)
    const declined = sortGuests(declinedGuests, sortOption)
    const awaiting = sortGuests(awaitingGuests, sortOption)

    return { accepted, declined, awaiting }
  }, [guests, statusKey, sortOption, sortGuests])

  // Sort invited guests separately
  const sortedInvitedGuestList = useMemo(() => {
    return sortGuests(invitedGuestList || [], sortOption)
  }, [invitedGuestList, sortOption, sortGuests])

  const getMessageStatus = useCallback(
    (item) => {
      if (!item) return EMessageStatus.INVITED
      if (!item.phoneNumber) {
        return EMessageStatus.INVITED
      }
      const guestMessage = messageRecords?.filter((e) =>
        e.sentTo.includes(item.phoneNumber),
      )

      if (guestMessage?.length > 0) {
        const hasSent = sentStatuses.some((value) =>
          guestMessage[0].statuses.includes(value),
        )
        if (hasSent) {
          return EMessageStatus.INVITED
        }
        const failed = failedStatuses.some((value) =>
          guestMessage[0].statuses.includes(value),
        )
        if (failed) return EMessageStatus.FAILED
      }
      return EMessageStatus.SENDING
    },
    [messageRecords, sentStatuses, failedStatuses],
  )

  const filterGuests = useCallback((guests: Guest[], searchText: string) => {
    if (!searchText) return guests

    const searchLower = searchText.toLowerCase()
    return guests.filter((guest) =>
      guest.name.toLowerCase().includes(searchLower),
    )
  }, [])

  const debounceRef = useRef<NodeJS.Timeout | null>(null)

  const debouncedFilter = useCallback(
    (
      searchText: string,
      currentGuestList: Guest[],
      currentInvitedList: Guest[],
    ) => {
      // Clear any existing timeout
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }

      // If search is empty, update immediately
      if (!searchText.trim()) {
        setGuests(currentGuestList)
        setInvitedGuestList(currentInvitedList)
        return
      }

      // Otherwise, debounce the filtering
      debounceRef.current = setTimeout(() => {
        const filteredResults = filterGuests(currentGuestList, searchText)
        const filteredResultsInvited = filterGuests(
          currentInvitedList,
          searchText,
        )
        setGuests(filteredResults)
        setInvitedGuestList(filteredResultsInvited)
      }, 300)
    },
    [filterGuests],
  )

  // Update guests when guestlist, invitedGuests, or searchText change
  useEffect(() => {
    debouncedFilter(searchText, guestlist, invitedGuests)
  }, [guestlist, invitedGuests, searchText, debouncedFilter])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [])

  // Memoize tabs with proper dependencies
  const tabs = useMemo(() => {
    const tabsArray = [
      {
        key: "awaiting",
        title: `Awaiting`,
        content: awaiting,
        shouldRender: guestListWeb.isAwaitingEnabled,
      },
      {
        key: "invited",
        title: `Invited`,
        content: sortedInvitedGuestList,
        shouldRender: guestListWeb.isInvitedEnabled,
      },
      {
        key: "going",
        title: "Going",
        content: accepted,
        shouldRender: guestListWeb.isGoingEnabled,
      },
      {
        key: "not_going",
        title: `Not Going`,
        content: declined,
        shouldRender: guestListWeb.isNotGoingEnabled,
      },
    ]

    const awaitingTab = tabsArray.find((tab) => tab.key === "awaiting")
    const otherTabs = tabsArray.filter((tab) => tab.key !== "awaiting")

    if (awaiting?.length > 0) {
      return [awaitingTab!, ...otherTabs]
    } else {
      return [...otherTabs, awaitingTab!]
    }
  }, [awaiting, sortedInvitedGuestList, accepted, declined])

  // Initialize activeTab with restored tab from location state or default
  const [activeTab, setActiveTab] = useState(() => {
    // Check if we have a restored tab from navigation state
    const restoredTabKey = locationState?.restoredTab
    if (restoredTabKey) {
      const restoredTab = tabs.find(
        (tab) => tab.key === restoredTabKey && tab.shouldRender,
      )
      if (restoredTab) {
        return restoredTab
      }
    }
    // Fallback to default logic
    return tabs.find((tab) => tab.shouldRender) || tabs[0]
  })

  // Update activeTab when tabs change to ensure we're showing the correct content
  useEffect(() => {
    const currentTab = tabs.find((tab) => tab.key === activeTab.key)
    if (currentTab && currentTab.content !== activeTab.content) {
      setActiveTab(currentTab)
    }
  }, [tabs, activeTab.key])

  // Effect to restore tab when coming back from questionnaire review
  useEffect(() => {
    const restoredTabKey = locationState?.restoredTab
    if (restoredTabKey) {
      const restoredTab = tabs.find(
        (tab) => tab.key === restoredTabKey && tab.shouldRender,
      )
      if (restoredTab && restoredTab.key !== activeTab.key) {
        setActiveTab(restoredTab)
      }
    }
  }, [])

  const [isSortOpen, setIsSortOpen] = useState(false)

  // Handle sort option selection
  const handleSortSelection = (option: SortOption) => {
    setSortOption(option)
    setIsSortOpen(false)
  }

  const [currentGuest, setCurrentGuest] = useState<Guest | null>(null)

  // Static sort display text
  const getSortDisplayText = () => {
    return "Sort by"
  }

  const reviewGuestsBottomSheetRef = useRef<ReviewGuestsHandle>(null)
  const onOpenReviewGuestsBottomSheet = () => {
    reviewGuestsBottomSheetRef.current?.openModal()
  }

  const sortButtonRef = useRef<HTMLDivElement | null>(null)
  const [sortDropdownPosition, setSortDropdownPosition] = useState<{
    top: number
    left: number
  }>({
    top: 0,
    left: 0,
  })
  const updateSortDropdownPosition = () => {
    const rect = sortButtonRef.current?.getBoundingClientRect()
    if (rect) {
      setSortDropdownPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX,
      })
    }
  }

  useEffect(() => {
    if (isSortOpen) {
      updateSortDropdownPosition()
    }
  }, [isSortOpen])

  useEffect(() => {
    if (!isSortOpen) return

    const handleResizeOrScroll = () => updateSortDropdownPosition()

    window.addEventListener("resize", handleResizeOrScroll)
    window.addEventListener("scroll", handleResizeOrScroll, true)

    return () => {
      window.removeEventListener("resize", handleResizeOrScroll)
      window.removeEventListener("scroll", handleResizeOrScroll, true)
    }
  }, [isSortOpen])

  const renderGuestItem = useCallback(
    (guest: any, status: string, key: string) => {
      const messageStatus = getMessageStatus(guest)
      return (
        <div
          key={guest.uid || guest.id} // Add a key for React rendering
          className="w-full flex items-center justify-between cursor-pointer "
          onClick={() => {
            if (status === EventStatusType.LEFT) return
            setCurrentGuest({ ...guest, status })
            onOpenReviewGuestsBottomSheet()
          }}
        >
          <div className="flex items-center gap-4 flex-1">
            {guest?.avatarUrl ? (
              <img
                src={guest?.avatarUrl}
                alt="avatar"
                className="w-14 h-14 rounded-full"
              />
            ) : (
              <div className="w-14 h-14 rounded-full flex items-center justify-center text-white text-sm bg-white/20">
                {guest?.name
                  ? guest?.name[0]?.toUpperCase()
                  : guest?.email
                    ? guest?.email[0]?.toUpperCase()
                    : "A"}
              </div>
            )}

            <div className="flex-1">
              <div className="text-white font-medium text-base text-left ">
                {guest?.name || "Anonymous"}
                {guest?.numberOfExtraJoinee > 0 && (
                  <span className="text-sm text-white/70 ">
                    {` +${guest.numberOfExtraJoinee}`}
                  </span>
                )}
              </div>

              {key === "invited" && (
                <p className="text-sm text-white/60 truncate">
                  {`${getDialCode(guest?.country)} ${guest?.phoneNumber || ""}${
                    guest?.phoneNumber && guest?.email ? " | " : ""
                  }${guest?.email?.toLowerCase() || ""}`}
                </p>
              )}
            </div>

            {key === "invited" ? (
              <div className="flex items-center gap-2 w-24 pl-6">
                <div
                // className={classNames("w-2 h-2 rounded-full", {
                //   "bg-blue-600": messageStatus === "INVITED",
                //   "bg-sky-400": messageStatus === "SENDING",
                //   "bg-red-500": messageStatus === "FAILED",
                //   "bg-white/50": !["INVITED", "SENDING", "FAILED"].includes(
                //     messageStatus
                //   ),
                // })}
                />
                <span className="text-sm text-white/60">{messageStatus}</span>
              </div>
            ) : (
              <>
                {(status === EventStatusType.ATTENDING ||
                  status === EventStatusType.ACCEPTED) && (
                  <div className="border border-white rounded px-3 py-1 w-[103px] flex items-center justify-center">
                    <span className="text-white text-sm">Attending</span>
                  </div>
                )}
                {status === EventStatusType.CHECKED_IN && (
                  <div className=" bg-white/10 rounded px-2.5 py-1 w-[103px] flex items-center justify-center h-[30px] ">
                    <span className="text-white text-sm">Checked in</span>
                  </div>
                )}
                {status === EventStatusType.UNDER_REVIEW && (
                  <div className="flex gap-2 cursor-pointer">
                    <button className=" bg-white/10 rounded-full px-6 py-2 w-[103px] text-white">
                      Review
                    </button>
                  </div>
                )}
                {(status === EventStatusType.DECLINED ||
                  status === EventStatusType.USER_DECLINED) && (
                  <div className="border border-white rounded px-3 py-1 w-[108px] flex items-center justify-center">
                    <span className="text-white text-sm text-center">
                      {status === EventStatusType.USER_DECLINED
                        ? "Declined"
                        : "Not Approved"}
                    </span>
                  </div>
                )}
                {status === EventStatusType.LEFT && (
                  <div className="border border-white rounded px-3 py-1 w-[103px] flex items-center justify-center h-[30px]">
                    <span className="text-white text-sm">Left</span>
                  </div>
                )}
                {status === EventStatusType.REFUND_REQUESTED && (
                  <div className="border border-white rounded px-3 py-1 w-[103px] flex items-center justify-center h-[30px]">
                    <span className="text-white text-sm truncate">
                      REFUND_REQUESTED
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )
    },
    [getMessageStatus],
  )

  const renderGuestList = useCallback(
    (tab: any) => {
      if (!tab.shouldRender) return null
      return (
        <div className="w-full flex flex-col gap-6 ">
          <div className="flex justify-between">
            <div className="flex gap-4 justify-between items-center w-full">
              <div className="text-white font-medium text-xl">
                {`${tab.title} `}
                <span className="text-white/30">{`${"\u00A0"} ${tab?.content?.length > 0 ? `(${tab?.content?.length})` : ""}`}</span>
              </div>
              <div
                ref={sortButtonRef}
                className="flex gap-2 items-center justify-center bg-white/10 rounded-xl p-2 w-auto min-w-32 relative cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation()
                  setIsSortOpen(!isSortOpen)
                }}
              >
                <MdSort color="#fff" />
                <span className=" flex-1 text-left text-base text-white">
                  Sort by
                </span>
                <IoIosArrowDown
                  color="#fff"
                  className={`transform transition-transform ${isSortOpen ? "rotate-180" : ""}`}
                />
              </div>
            </div>
          </div>
          {tab?.content?.length > 0 ? (
            <div className="flex flex-col gap-6">
              {tab?.content?.map((guest) =>
                renderGuestItem(guest, guest[statusKey], tab.key),
              )}
            </div>
          ) : guestlist.length === 0 && isGuestListLoading ? (
            <GuestListSkeleton />
          ) : (
            <div className="flex-1 flex justify-center items-center mt-4">
              <EmptyGuestList
                isSearching={searchText.length > 0}
                tab={tab.key}
              />
            </div>
          )}
        </div>
      )
    },
    [
      renderGuestItem,
      statusKey,
      sortOption,
      isSortOpen,
      getSortDisplayText,
      handleSortSelection,
    ],
  )

  return (
    <BackgroundWrapper bgImage={flyerImage} className="min-h-screen">
      {isLoading && <LoadingSpinner />}
      <BlurWrapper
        blurKey={get(blurKeys, "mainBlurView")}
        overlayOpacityDefault="75"
        overlayColorDefault="dark"
        className=" bg-gray-400/10 h-[10vh] flex flex-row  items-center px-6  top-0 left-0 w-full z-10 fixed "
      >
        <div
          className="text-white/30 text-base cursor-pointer p-4 rounded-full bg-white/10 absolute left-6"
          onClick={handleBackPress}
        >
          <IoIosArrowBack className="text-white" />
        </div>
        <div className="text-white text-base cursor-pointer flex-1">
          Guest list
        </div>
      </BlurWrapper>
      <BlurWrapper
        blurKey={get(blurKeys, "mainBlurView")}
        overlayOpacityDefault="75"
        overlayColorDefault="dark"
        className="min-h-[100vh] h-full flex flex-col relative overflow-auto"
      >
        <div
          className="flex flex-col justify-center items-center p-6 w-full max-w-2xl mx-auto mt-[10vh] gap-6  flex-1"
          onClick={(e) => {
            if (isSortOpen) {
              setIsSortOpen(false)
            }
          }}
        >
          <div className="px-4 py-2 rounded-2xl bg-white/10 w-full flex gap-4 items-center">
            <IoSearch className="text-white/30" size={22} />
            <input
              className="!bg-transparent text-white placeholder-white/30 outline-none w-full"
              onChange={(e) => {
                setSearchText(e.target.value)
              }}
              value={searchText}
              placeholder="Search by name"
            />
          </div>

          <div className="p-1 rounded-xl bg-white/10 w-full flex flex-col gap-4 items-center">
            <div className="w-full">
              <div className="flex  space-x-4 overflow-x-auto  relative no-scrollbar">
                {tabs.map(
                  (tab) =>
                    tab.shouldRender && (
                      <button
                        key={tab.key}
                        onClick={() => {
                          setSortOption(SortOption.NAME_A_Z)
                          setActiveTab(tab)
                        }}
                        className={`relative px-4 py-2 rounded-lg whitespace-nowrap min-w-[100px] cursor-pointer flex-1 ${
                          activeTab.key === tab.key
                            ? "bg-transparent text-white"
                            : "bg-transparent text-white/30"
                        }`}
                      >
                        {activeTab.key === tab.key && (
                          <motion.div
                            layoutId="tabHighlight"
                            className="absolute inset-0 rounded-lg bg-white/30 z-[-1]"
                            transition={{
                              type: "spring",
                              stiffness: 500,
                              damping: 30,
                            }}
                          />
                        )}
                        {tab.title}
                      </button>
                    ),
                )}
              </div>
            </div>
          </div>
          <div className="w-full flex-1 gap-2 flex flex-col">
            {renderGuestList(activeTab)}
          </div>
        </div>
      </BlurWrapper>
      <ReviewGuests
        ref={reviewGuestsBottomSheetRef}
        guest={currentGuest}
        eventId={eventId}
        handleApproveGuest={handleApproveGuest}
        handleDisapproveGuest={handleDisapproveGuest}
        handleCheckInGuest={handleCheckInGuest}
        isTicketedEvent={isTicketedEvent}
        eventDetails={eventDetails}
        currentTab={activeTab.key}
      />

      {isSortOpen && (
        <div
          className="absolute w-32 bg-white/10 backdrop-blur-md rounded-xl z-[9999] flex flex-col gap-2 p-3 shadow-lg"
          style={{
            position: "absolute",
            top: sortDropdownPosition.top,
            left: sortDropdownPosition.left,
          }}
        >
          <div
            className={`text-white text-left cursor-pointer hover:bg-white/10 p-2 rounded ${
              sortOption === SortOption.NAME_A_Z ? "bg-white/10" : ""
            }`}
            onClick={() => handleSortSelection(SortOption.NAME_A_Z)}
          >
            Name (A-Z)
          </div>
          <div
            className={`text-white text-left cursor-pointer hover:bg-white/10 p-2 rounded ${
              sortOption === SortOption.NAME_Z_A ? "bg-white/10" : ""
            }`}
            onClick={() => handleSortSelection(SortOption.NAME_Z_A)}
          >
            Name (Z-A)
          </div>
          {/* <div
                      className={`text-white text-sm text-left cursor-pointer hover:bg-white/10 p-2 rounded ${
                        sortOption === SortOption.MOST_RECENT
                          ? "bg-white/10"
                          : ""
                      }`}
                      onClick={() =>
                        handleSortSelection(SortOption.MOST_RECENT)
                      }
                    >
                      Most Recent
                    </div> */}
        </div>
      )}
    </BackgroundWrapper>
  )
}

export default GuestListCore
